<template>
  <div class="to-live">
    <div class="to-live__loader" v-if="isLoading || !hasLoaded">
      <Loader bgColor="#ffffff"></Loader>
    </div>
    <template v-else>
      <TaskOverviewUser
        v-if="hasContent"
        :user="user"
        :version="liveVersion"
        :showHeadline="false"
        :canEditTasks="canEditTasks"
        :canEditNotes="canEditNotes"
        @editTask="setEditSubject">
      </TaskOverviewUser>
      <p class="to-live__empty text-explainer" v-else>{{ i18n[locale]['no_tasks'] }}</p>
    </template>
    <SubjectForm
      v-if="isSubjectModal"
      :showModal="isSubjectModal"
      :editSubject="editSubject"
      :propIsLoading="isSubjectLoading"
      :allowDanishPreparationFactorUi="true"
      @deleteSubject="deleteSubject"
      @close="closeSubjectModal"
      @updateSubject="updateSubject"
      @saveAndShowTab="saveAndShowTab">
    </SubjectForm>
  </div>
</template>

<script>
import moment from 'moment'
import docendo from '../../../docendo'
import eventbus from '../../../eventbus'
import Loader from '../../common/Loader'
import TaskOverviewUser from './TaskOverviewUser'
import SubjectForm from '../../Subjects/SubjectForm'

const I18N = {
  da: {
    no_tasks: 'Ingen opgaver at vise'
  },
  en: {
    no_tasks: 'No tasks to show'
  },
  nb: {
    no_tasks: 'Ingen oppgaver å vise'
  }
}

export default {
  components: {Loader, TaskOverviewUser, SubjectForm},
  props: {
    user: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      isLoading: true,
      hasLoaded: false,
      tasks: [],
      userYear: null,
      debounceTimer: null,
      subjectsChangedListener: null,
      isSubjectModal: false,
      isSubjectLoading: false,
      editSubject: null,
      pendingSubjectTab: null,
      pendingSubjectId: null
    }
  },
  computed: {
    locale () {
      return this.$store.getters.locale
    },
    i18n () {
      return I18N
    },
    schoolId () {
      return this.$store.getters.schoolId
    },
    schoolYearId () {
      return this.$store.getters.schoolYearId
    },
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    fromDate () {
      return this.activeSchoolYear ? this.activeSchoolYear.employee_time_start : null
    },
    toDate () {
      return this.activeSchoolYear ? this.activeSchoolYear.employee_time_end : null
    },
    schoolSettings () {
      return this.$store.state.schoolSettings || {}
    },
    shownOwnPools () {
      return this.$store.getters.shownOwnPools
    },
    tasksFiltered () {
      // api.users.tasks returnerer task_overview-flaget pr. række, så filtreringen
      // matcher "Gem version" 1:1 - også for skemalagte lektioner og vikartimer
      return this.tasks.filter(task => task.task_overview !== false)
    },
    userNorm () {
      return this.userYear && this.userYear.norm ? this.userYear.norm : {}
    },
    userPools () {
      // Samme mapping som TaskOverview#pools i backend
      const uy = this.userYear || {}
      const toNumberOrNull = val => (val === null || val === undefined ? null : parseFloat(val))
      return {
        assigned_total_hours: toNumberOrNull(uy.assigned_total_hours),
        assigned_teaching_hours: toNumberOrNull(uy.assigned_teaching_hours),
        assigned_preparation_hours: toNumberOrNull(uy.assigned_preparation_hours),
        substitute_pool_hours: toNumberOrNull(uy.substitute_pool_hours),
        assigned_additional_task_hours: toNumberOrNull(uy.assigned_additional_task_hours),
        assigned_club_hours: toNumberOrNull(uy.assigned_club_hours),
        assigned_pool_hours: toNumberOrNull(uy.assigned_pool_hours)
      }
    },
    hasContent () {
      const hasNorm = Array.isArray(this.userNorm) && this.userNorm.length > 0
      const hasPools = Object.values(this.userPools).some(val => val !== null && val !== 0)
      const hasAgreements = !!(this.userYear && this.userYear.agreements)
      return this.tasksFiltered.length > 0 || hasNorm || hasPools || hasAgreements || this.canEditNotes
    },
    theUser () {
      return this.$store.state.user
    },
    isPermissionsActivatedForSchool () {
      return this.$store.getters.isPermissionsActivatedForSchool
    },
    userPermissions () {
      return this.$store.getters.userPermissions
    },
    canEditTasks () {
      return this.userHasPermission('can_manage_school')
    },
    canEditNotes () {
      return this.userHasPermission('can_edit_user_year_notes')
    },
    liveVersion () {
      // Pseudo-version i samme format som en gemt TaskOverview (se TaskOverview#generate_overview
      // i backend), så TaskOverviewUser kan genbruges 1:1 - blot med live-data.
      return {
        school_year_id: this.schoolYearId,
        created_at: null,
        overview: {
          norm: this.userNorm,
          pools: this.userPools,
          tasks: this.tasksFiltered,
          agreements: this.userYear && this.userYear.agreements ? this.userYear.agreements : '',
          school_custom_data: {
            deactivatePools: this.schoolSettings.deactivatePools,
            shownOwnPools: this.shownOwnPools,
            thresholdToHideTaskHours: this.schoolSettings.thresholdToHideTaskHours
          }
        }
      }
    }
  },
  watch: {
    'user.id' () {
      this.isLoading = true
      this.readData()
    },
    schoolYearId () {
      this.isLoading = true
      this.readData()
    },
    toDate (val) {
      if (val && !this.hasLoaded) {
        this.readData()
      }
    }
  },
  methods: {
    userHasPermission (permission) {
      if (!this.theUser) {
        return false
      }
      if (this.isPermissionsActivatedForSchool) {
        return this.userPermissions.indexOf(permission) > -1 || this.theUser.admin
      }
      return true
    },
    setEditSubject (task) {
      if (!task || !task.id) {
        return
      }
      this.isSubjectLoading = true
      this.isSubjectModal = true
      this.editSubject = null
      docendo.showSubject(task.id, success => {
        if (success && success.status === 'error') {
          this.closeSubjectModal()
          eventbus.toast = {text: success.message || this.$t('vue2.common.something_went_wrong'), success: false}
          return
        }
        const pendingTab = this.pendingSubjectTab
        this.pendingSubjectTab = null
        this.pendingSubjectId = null
        this.editSubject = pendingTab
          ? Object.assign({}, success, {_openTab: pendingTab})
          : Object.assign({}, success)
        this.isSubjectLoading = false
      }, failure => {
        this.closeSubjectModal()
        eventbus.toast = {text: this.$t('vue2.common.something_went_wrong'), success: false}
      })
    },
    closeSubjectModal () {
      this.editSubject = null
      this.isSubjectModal = false
      this.isSubjectLoading = false
    },
    updateSubject (updatedSubject) {
      if (!updatedSubject.schoolId) {
        updatedSubject.schoolId = this.schoolId
      }
      if (!updatedSubject.schoolYearId) {
        updatedSubject.schoolYearId = this.schoolYearId
      }
      this.$store.dispatch('updateSubject', updatedSubject)
      this.closeSubjectModal()
    },
    deleteSubject (editSubject) {
      const deletedSubject = {
        id: editSubject.id,
        school_id: editSubject.school_id,
        school_year_id: this.schoolYearId,
        schoolYearId: this.schoolYearId
      }
      this.$store.dispatch('deleteSubject', deletedSubject)
      this.closeSubjectModal()
    },
    saveAndShowTab (updatedSubject, tab) {
      this.updateSubject(updatedSubject)
      this.pendingSubjectTab = tab
      this.pendingSubjectId = updatedSubject.id
      setTimeout(() => {
        this.setEditSubject({id: this.pendingSubjectId})
      }, 500)
    },
    readData () {
      this.isLoading = true
      if (!this.schoolId || !this.schoolYearId || !this.user || !this.user.id || !this.toDate) {
        return
      }
      let pending = 2
      const done = _ => {
        pending--
        if (pending === 0) {
          this.hasLoaded = true
          this.isLoading = false
        }
      }
      const toDateExclusive = moment(this.toDate).add(1, 'days').format('YYYY-MM-DD')
      docendo.userTasks(this.user.id, this.fromDate, toDateExclusive, true, data => {
        if (data && data.status === 'error') {
          done()
          return
        }
        this.tasks = data || []
        done()
      }, failure => {
        console.log('failed to read user tasks', failure)
        done()
      })
      // UserYear indeholder norm, tildelte timer (fordeling) og noter - samme kilde
      // som backend bruger, når et snapshot gemmes
      docendo.schoolUserYearRead(this.schoolId, this.user.id, this.activeSchoolYear.name, data => {
        if (data && data.status === 'error') {
          done()
          return
        }
        this.userYear = data || null
        done()
      }, failure => {
        console.log('failed to read user year', failure)
        done()
      })
    },
    readDataDebounced () {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(_ => {
        this.readData()
      }, 250)
    }
  },
  mounted () {
    this.readData()
    // Opdater visningen når subjects oprettes/opdateres/slettes andre steder i appen
    this.subjectsChangedListener = _ => {
      this.readDataDebounced()
    }
    window.addEventListener('vueCRUDSubject', this.subjectsChangedListener, false)
  },
  beforeDestroy () {
    clearTimeout(this.debounceTimer)
    window.removeEventListener('vueCRUDSubject', this.subjectsChangedListener, false)
  }
}
</script>

<style scoped>
.to-live__loader {
  background-color: #ffffff;
  min-height: 200px;
  padding: 2rem 0;
}

.to-live__empty {
  background-color: #ffffff;
  padding: 1rem;
  margin: 0;
}
</style>



// WEBPACK FOOTER //
// TaskOverviewLive.vue?fe5e3eee